import { MouseEvent, RefObject, useCallback, useEffect, useRef } from "react";

/**
 * How long a card has to sit still before it takes a tap. A card lands with
 * Kai's reply and then moves: the list scrolls to it (smooth, ~400 ms) and
 * text can still render around it. A tap aimed at one row then landed on
 * another (QA N4: a tap meant for a reason chip ticked an item instead).
 */
export const CARD_SETTLE_MS = 450;

/** How many ancestors are watched for size changes (the message, the list, the scroller). */
const WATCHED_ANCESTORS = 8;

/**
 * Keeps a card from taking clicks while it moves on screen. Returns the
 * card's `onClickCapture`: a click that arrives while the card is still
 * moving, or within CARD_SETTLE_MS of it stopping, is swallowed before any
 * control in the card sees it (no tick, no chip, no submit). The card is
 * "moving" when it just appeared, when anything scrolls and its position
 * changed, or when the list around it changed size and moved it.
 */
export const useSteadyCard = (ref: RefObject<HTMLElement | null>) => {
  // A card that just appeared counts as moving: the list scrolls to it next.
  const movedAt = useRef(performance.now());

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let lastTop = element.getBoundingClientRect().top;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const node = ref.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      if (Math.abs(top - lastTop) > 1) movedAt.current = performance.now();
      lastTop = top;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // Any scroller (the message list, the page) moving it.
    document.addEventListener("scroll", schedule, { capture: true, passive: true });
    // Content growing or shrinking around it (text rendering above, a footer appearing).
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    let ancestor: HTMLElement | null = element.parentElement;
    for (let i = 0; observer && ancestor && ancestor !== document.body && i < WATCHED_ANCESTORS; i++) {
      observer.observe(ancestor);
      ancestor = ancestor.parentElement;
    }
    return () => {
      document.removeEventListener("scroll", schedule, { capture: true });
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);

  return useCallback((event: MouseEvent) => {
    if (performance.now() - movedAt.current >= CARD_SETTLE_MS) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);
};
